import type { NoteFile } from './model';
export interface PendingAttachment { id: string; file: File; uploaded?: NoteFile }
export interface NoteDraft { key: string; requestId: string; text: string; files: PendingAttachment[]; attempted: boolean; audience: string; updatedAt: number }
const generations = new Map<string, number>();
const ownerOf = (key: string) => key.slice(0, key.lastIndexOf(':'));
export const draftGeneration = (key: string) => generations.get(ownerOf(key)) ?? 0;
let database: Promise<IDBDatabase> | null = null;
function open() {
  return database ??= new Promise((resolve, reject) => {
    const request = indexedDB.open('boss-pilot-project-note-drafts', 1);
    request.onupgradeneeded = () => request.result.createObjectStore('drafts', { keyPath: 'key' });
    request.onsuccess = () => { request.result.onversionchange = () => { request.result.close(); database = null; }; resolve(request.result); };
    request.onerror = () => { database = null; reject(request.error); };
  });
}
async function transaction<T>(mode: IDBTransactionMode, operation: (store: IDBObjectStore) => IDBRequest<T>): Promise<T> {
  const db = await open();
  return new Promise((resolve, reject) => {
    const tx = db.transaction('drafts', mode), request = operation(tx.objectStore('drafts'));
    tx.oncomplete = () => resolve(request.result); tx.onerror = tx.onabort = () => reject(tx.error ?? request.error);
  });
}
export async function readDraft(key: string): Promise<NoteDraft | undefined> { return transaction('readonly', store => store.get(key)); }
export async function writeDraft(draft: NoteDraft, generation: number) {
  if (draft.files.length > 5 || draft.files.reduce((sum, f) => sum + f.file.size, 0) > 5 * 200 * 1048576) throw new Error('DRAFT_TOO_LARGE');
  await open();
  if (generation !== draftGeneration(draft.key)) throw new Error('DRAFT_SESSION_ENDED');
  await transaction('readwrite', store => {
    if (generation !== draftGeneration(draft.key)) throw new Error('DRAFT_SESSION_ENDED');
    return store.put(draft);
  });
}
export async function removeDraft(key: string) { await transaction('readwrite', store => store.delete(key)); }

/** Logout clears only this account's unsent notes, never another account's drafts. */
export async function clearNoteDrafts(owner: string) {
  generations.set(owner, (generations.get(owner) ?? 0) + 1);
  const db = await open();
  await new Promise<void>((resolve, reject) => {
    const tx = db.transaction('drafts', 'readwrite'), store = tx.objectStore('drafts'), cursor = store.openCursor();
    cursor.onsuccess = () => { const c = cursor.result; if (!c) return; if (String(c.key).startsWith(`${owner}:`)) c.delete(); c.continue(); };
    tx.oncomplete = () => resolve(); tx.onerror = tx.onabort = () => reject(tx.error);
  });
}
